/**
 * O badge numerado sobre os elementos marcados — o que torna a funcionalidade compreensível
 * sem abrir painel nenhum.
 *
 * É o padrão do Prezi e do PowerPoint, e o molde é o `FrameOverlay` do próprio
 * `frame-manager.ts:31`: um `Overlay` desenha no MESMO canvas da superfície, já em
 * coordenadas do documento, e acompanha zoom e pan sem uma linha de código de sincronização.
 *
 * ⚠️ **Só aparece na EDIÇÃO.** Durante a apresentação ele sumiria de qualquer forma junto com
 * o elemento oculto, mas o passo já revelado ainda mostraria o número — e o público veria um
 * "3" azul flutuando sobre o slide. `render()` sai cedo quando a ferramenta de apresentação
 * está ativa.
 */

import { Overlay, OverlayIdentifier } from '@blocksuite/affine-block-surface';
import type { FrameBlockModel } from '@blocksuite/affine-model';
import { Bound } from '@blocksuite/global/gfx';
import type { GfxController } from '@blocksuite/std/gfx';

import { EdgelessFrameManagerIdentifier } from '../frame-manager';
import { PresentTool } from '../present-tool';
import { passosDoFrame } from './marcacao';

const RAIO = 11;
const COR = '#1E96EB';

export class BadgeDePassosOverlay extends Overlay {
  static override overlayName = 'tuninho-passos';

  private get _frameMgr() {
    return this.gfx.std.get(EdgelessFrameManagerIdentifier);
  }

  override render(ctx: CanvasRenderingContext2D): void {
    if (this.gfx.tool.currentToolOption$.peek()?.toolType === PresentTool) return;

    const zoom = this.gfx.viewport.zoom;

    for (const frame of this._frameMgr.frames as FrameBlockModel[]) {
      for (const passo of passosDoFrame(frame)) {
        const modelo = this.gfx.getElementById(passo.id);
        if (!modelo) continue;

        const xywh = (modelo as { xywh?: string }).xywh;
        if (!xywh) continue;

        const bound = Bound.deserialize(xywh);

        // O badge fica no canto superior ESQUERDO, para fora — é onde a barra de ferramentas
        // do elemento não passa (ela abre acima do centro) e onde o Prezi o coloca.
        const cx = bound.x;
        const cy = bound.y;
        const r = RAIO / zoom;

        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fillStyle = COR;
        ctx.fill();

        ctx.fillStyle = '#FFFFFF';
        ctx.font = `600 ${(12 / zoom).toFixed(2)}px Inter, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(passo.numero), cx, cy);
        ctx.restore();
      }
    }
  }
}

/**
 * O overlay deste editor — para pedir um redesenho depois de marcar/desmarcar.
 *
 * `OverlayIdentifier`, e não `GfxExtensionIdentifier`: é assim que `Overlay.setup()` se
 * registra (`renderer/overlay.ts:33`), e é essa lista que o `surface-block.ts:135` percorre
 * para entregar o renderer a cada overlay. Pedir pelo identificador errado devolveria `null`
 * sempre — e o badge simplesmente não se atualizaria, sem erro nenhum.
 */
export function badgeDePassos(gfx: GfxController): BadgeDePassosOverlay | null {
  return (
    (gfx.std.getOptional(
      OverlayIdentifier(BadgeDePassosOverlay.overlayName)
    ) as BadgeDePassosOverlay | null) ?? null
  );
}
